'use client'

import { useEffect, useRef } from 'react'
import { useParams, usePathname } from 'next/navigation'

import { isAcceptInvitationRoute } from '@common/utils/isAcceptInvitationRoute'
import { isPortalAppOnClient } from '@common/utils/isPortalApp'
import { redirectToPortal } from '@common/utils/redirectToPortal'

import { selectCurrentProject } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'

type UseFirstTimeUserPortalRedirectParams = {
  isEmailVerified: boolean
  isInitializing: boolean
  isOnboarding: boolean
}

export function useFirstTimeUserPortalRedirect({
  isEmailVerified,
  isInitializing,
  isOnboarding,
}: UseFirstTimeUserPortalRedirectParams) {
  const pathname = usePathname()
  const { locale } = useParams()
  const currentWorkspace = useWorkspacesStore((s) => s.currentWorkspace)
  const currentProject = useProjectsStore(selectCurrentProject)
  const isResolvingWorkspaceProject = useProjectsStore((s) => s.isResolvingWorkspaceProject)
  const redirectStartedRef = useRef(false)

  useEffect(() => {
    if (isPortalAppOnClient()) return
    if (isAcceptInvitationRoute(pathname)) return
    if (!isEmailVerified || isInitializing || isOnboarding) return
    if (isResolvingWorkspaceProject) return
    if (currentWorkspace && currentProject) return
    if (redirectStartedRef.current) return

    redirectStartedRef.current = true
    redirectToPortal('', typeof locale === 'string' ? locale : 'en')
  }, [
    pathname,
    locale,
    isEmailVerified,
    isInitializing,
    isOnboarding,
    isResolvingWorkspaceProject,
    currentWorkspace,
    currentProject,
  ])
}
